import { queryOptions, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import defaultTo from 'lodash/defaultTo';
import toNumber from 'lodash/toNumber';

import {
  CostReport,
  CostReportRaw,
  CostReportSummary,
  CostReportSummaryRaw,
} from '@cast/types';
import { toBig } from '@cast/utils';

import { apiClient } from '@cast/infrastructure-api';
import { DataGranularity } from 'types/cost-report';
import { granularityToSeconds } from 'utils/costReport';

const CLUSTER_COST_REPORT_QUERY_KEY = 'cluster-cost-report';

const getCostReportQueryKey = ({
  id,
  startTime,
  endTime,
  useListingPrices,
  stepSeconds,
}: {
  id: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  stepSeconds: number;
}) =>
  [
    CLUSTER_COST_REPORT_QUERY_KEY,
    id,
    startTime,
    endTime,
    useListingPrices,
    stepSeconds,
  ] as const;

const toCostNumber = (value?: string): number =>
  value ? toBig(value).toNumber() : 0;

export const enhanceCostReportItemData = (
  rawData: CostReportRaw,
  granularity: DataGranularity
): CostReport => {
  return {
    timestamp: dayjs
      .utc(rawData.timestamp)
      .subtract(granularity.step, granularity.unit)
      .toISOString(),
    costOnDemand: toCostNumber(rawData.costOnDemand),
    costSpot: toCostNumber(rawData.costSpot),
    costSpotFallback: toCostNumber(rawData.costSpotFallback),
    // CPU related metrics
    cpuCostOnDemand: toCostNumber(rawData.cpuCostOnDemand),
    cpuCostSpot: toCostNumber(rawData.cpuCostSpot),
    cpuCostSpotFallback: toCostNumber(rawData.cpuCostSpotFallback),
    cpuCountOnDemand: Number(rawData.cpuCountOnDemand),
    cpuCountSpot: Number(rawData.cpuCountSpot),
    cpuCountSpotFallback: Number(rawData.cpuCountSpotFallback),
    // RAM related metrics
    ramCostOnDemand: toCostNumber(rawData.ramCostOnDemand),
    ramCostSpot: toCostNumber(rawData.ramCostSpot),
    ramCostSpotFallback: toCostNumber(rawData.ramCostSpotFallback),
    ramGibOnDemand: Number(rawData.ramGibOnDemand),
    ramGibSpot: Number(rawData.ramGibSpot),
    ramGibSpotFallback: Number(rawData.ramGibSpotFallback),
    // GPU related metrics
    gpuCostOnDemand: toCostNumber(rawData.gpuCostOnDemand),
    gpuCostSpot: toCostNumber(rawData.gpuCostSpot),
    gpuCostSpotFallback: toCostNumber(rawData.gpuCostSpotFallback),
    gpuCountOnDemand: Number(rawData.gpuCountOnDemand),
    gpuCountSpot: Number(rawData.gpuCountSpot),
    gpuCountSpotFallback: Number(rawData.gpuCountSpotFallback),
    // TPU related metrics
    tpuCostOnDemand: toCostNumber(rawData.tpuCostOnDemand),
    tpuCostSpot: toCostNumber(rawData.tpuCostSpot),
    tpuCostSpotFallback: toCostNumber(rawData.tpuCostSpotFallback),
    tpuCountOnDemand: Number(rawData.tpuCountOnDemand),
    tpuCountSpot: Number(rawData.tpuCountSpot),
    tpuCountSpotFallback: Number(rawData.tpuCountSpotFallback),
    storageCount: Number(rawData.storageGib || '0'),
    storageCost: Number(rawData.storageCost || '0'),
  };
};

const enhanceCostReportSummaryData = (
  rawSummaryData: CostReportSummaryRaw
): CostReportSummary => {
  // guarantees a number is returned
  const convertToNumber = (value?: string) => defaultTo(toNumber(value), 0);

  return Object.entries(rawSummaryData).reduce(
    (result, [key, value]) => ({
      ...result,
      [key]: convertToNumber(value),
    }),
    {} as CostReportSummary
  );
};

type UseCostReportQueryArgs = {
  id: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  enabled?: boolean;
  disableRefetch?: boolean;
  granularity: DataGranularity;
};

const getCostReportQueryOptions = ({
  id,
  startTime,
  endTime,
  useListingPrices,
  enabled = true,
  disableRefetch,
  granularity,
}: UseCostReportQueryArgs) => {
  let refetchInterval: boolean | number = false;
  if (!disableRefetch && dayjs().isBetween(startTime, endTime, null, '[]')) {
    refetchInterval = 30_000;
  }

  const stepSeconds = granularityToSeconds(granularity);

  return queryOptions({
    queryKey: getCostReportQueryKey({
      id,
      startTime,
      endTime,
      useListingPrices,
      stepSeconds,
    }),
    queryFn: async () => {
      // API returns range end, thus need to adjust start and end time
      const { data } = await apiClient.costReport.getCostReport(
        {
          clusterId: id,
          startTime,
          endTime,
          stepSeconds,
          useListingPrices,
        },
        { timeout: 30_000 }
      );

      const items = data?.items || [];
      const summary = data?.summary || {};

      // Normally, data enhancing is done in a separate "layer" to separate concerns.
      // However, in this case, it's a simple transformation to guarantee a value is returned.
      // Therefore, coupling is arguably reasonable and acceptable.
      // No further data processing should be done here.

      const itemsEnhanced = items.map((i) =>
        enhanceCostReportItemData(i, granularity)
      );

      const summaryEnhanced = enhanceCostReportSummaryData(summary);

      return { ...data, items: itemsEnhanced, summary: summaryEnhanced };
    },
    enabled: !!id && enabled,
    refetchInterval,
  });
};

export const useCostReportQuery = (args: UseCostReportQueryArgs) => {
  const { isLoading, data, error, refetch } = useQuery(
    getCostReportQueryOptions(args)
  );

  return {
    isLoading,
    costReport: data,
    error,
    refetch,
  };
};
